(function sunglensFirstPartyAnalytics() {
  'use strict';

  const script = document.currentScript;
  const endpointValue = script?.dataset.endpoint || '';
  const MAX_EVENTS_PER_SESSION = 200;
  const VISITOR_KEY = 'sunglens_analytics_visitor_id';
  const SESSION_KEY = 'sunglens_analytics_session_id';
  const EVENT_COUNT_KEY = 'sunglens_analytics_event_count';
  const CAMPAIGN_KEY = 'sunglens_analytics_campaign';
  const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  const PRIVATE_PATH_PATTERN = /^\/(?:account|checkout|checkouts|challenge)(?:\/|$)/i;
  const UTM_LIMITS = {
    utm_source: 128,
    utm_medium: 128,
    utm_campaign: 255,
    utm_content: 255,
    utm_term: 255,
  };

  if (!endpointValue) return;

  let endpoint;
  try {
    endpoint = new URL(endpointValue, window.location.origin);
  } catch (_) {
    return;
  }
  if (endpoint.protocol !== 'https:' || endpoint.username || endpoint.password) return;
  endpoint.search = '';
  endpoint.hash = '';

  function cleanText(value, maximumLength) {
    if (typeof value !== 'string') return null;
    const cleaned = value
      .replace(/[\u0000-\u001f\u007f]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    return cleaned ? cleaned.slice(0, maximumLength) : null;
  }

  function createUuid() {
    if (typeof window.crypto?.randomUUID === 'function') return window.crypto.randomUUID();
    const bytes = new Uint8Array(16);
    window.crypto.getRandomValues(bytes);
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0'))
      .join('')
      .replace(/^(.{8})(.{4})(.{4})(.{4})(.{12})$/, '$1-$2-$3-$4-$5');
  }

  function storedUuid(storage, key) {
    try {
      const existing = storage.getItem(key);
      if (existing && UUID_PATTERN.test(existing)) return existing;
      const created = createUuid();
      storage.setItem(key, created);
      return created;
    } catch (_) {
      return createUuid();
    }
  }

  function analyticsAllowed() {
    const privacy = window.Shopify?.customerPrivacy;
    if (typeof privacy?.analyticsProcessingAllowed !== 'function') return true;
    try {
      return privacy.analyticsProcessingAllowed();
    } catch (_) {
      return false;
    }
  }

  function reserveEvent() {
    try {
      const current = Number.parseInt(sessionStorage.getItem(EVENT_COUNT_KEY) || '0', 10);
      const count = Number.isFinite(current) && current >= 0 ? current : 0;
      if (count >= MAX_EVENTS_PER_SESSION) return false;
      sessionStorage.setItem(EVENT_COUNT_KEY, String(count + 1));
      return true;
    } catch (_) {
      return true;
    }
  }

  function campaignValues() {
    const params = new URLSearchParams(window.location.search);
    const current = {};
    let hasCurrentCampaign = false;
    Object.entries(UTM_LIMITS).forEach(([key, limit]) => {
      const value = cleanText(params.get(key), limit);
      current[key] = value;
      if (value) hasCurrentCampaign = true;
    });

    if (hasCurrentCampaign) {
      try {
        sessionStorage.setItem(CAMPAIGN_KEY, JSON.stringify(current));
      } catch (_) {
        // Storage may be unavailable in private browsing; the current URL still works.
      }
      return current;
    }

    try {
      const stored = JSON.parse(sessionStorage.getItem(CAMPAIGN_KEY) || '{}');
      return Object.fromEntries(
        Object.entries(UTM_LIMITS).map(([key, limit]) => [
          key,
          cleanText(stored?.[key], limit),
        ]),
      );
    } catch (_) {
      return current;
    }
  }

  function referrerHost() {
    if (!document.referrer) return null;
    try {
      const hostname = new URL(document.referrer).hostname.toLowerCase();
      if (!hostname || hostname === window.location.hostname.toLowerCase()) return null;
      return hostname.slice(0, 255);
    } catch (_) {
      return null;
    }
  }

  function deviceType() {
    const width = Math.max(0, Math.min(Number(window.screen?.width) || 0, 10000));
    if (!width) return 'unknown';
    if (width < 768) return 'mobile';
    if (width < 1024) return 'tablet';
    return 'desktop';
  }

  function targetPath(element) {
    const anchor = element.closest('a[href]');
    const form = element.closest('form[action]');
    const value = anchor?.getAttribute('href') || form?.getAttribute('action');
    if (!value || /^(?:javascript|mailto|tel):/i.test(value)) return null;
    try {
      const url = new URL(value, window.location.href);
      if (!/^https?:$/.test(url.protocol)) return null;
      const path = url.pathname.slice(0, 512) || '/';
      if (url.origin === window.location.origin) return path;
      if (url.protocol !== 'https:') return null;
      return url.origin.slice(0, 512);
    } catch (_) {
      return null;
    }
  }

  function classifyClick(element, path) {
    const form = element.closest('form');
    const marker = [
      element.getAttribute('name'),
      element.id,
      element.className,
      element.getAttribute('data-testid'),
      form?.getAttribute('action'),
    ]
      .filter((value) => typeof value === 'string')
      .join(' ')
      .toLowerCase();

    if (
      marker.includes('checkout')
      || marker.includes('shopify-payment-button')
      || path === '/checkout'
      || path?.startsWith('/checkouts/')
    ) return 'begin_checkout';
    if (
      marker.includes('add-to-cart')
      || marker.includes('add_to_cart')
      || element.getAttribute('name') === 'add'
      || marker.includes('/cart/add')
    ) return 'add_to_cart';
    if (path === '/cart') return 'view_cart';
    if (
      path === '/search'
      || marker.includes('/search')
      || Boolean(element.closest('[role="search"], search-modal, predictive-search'))
    ) return 'search';
    if (path?.startsWith('/products/')) return 'product_click';
    if (path?.startsWith('/collections/')) return 'collection_click';
    if (path?.startsWith('https://')) return 'external_link';
    if (element.closest('a[href]')) return 'navigation';
    return 'button_click';
  }

  function clickLabel(element, action, path) {
    const explicitLabel = cleanText(
      element.getAttribute('data-analytics-label') || element.getAttribute('aria-label'),
      255,
    );
    if (explicitLabel) return explicitLabel;

    const visibleLabel = cleanText(element.textContent, 255);
    if (visibleLabel && !visibleLabel.includes('@')) return visibleLabel;

    if (path?.startsWith('/products/') || path?.startsWith('/collections/')) {
      return cleanText(path.split('/').filter(Boolean).pop()?.replace(/-/g, ' '), 255);
    }
    return cleanText(action.replace(/_/g, ' '), 255);
  }

  function send(payload) {
    if (!analyticsAllowed() || !reserveEvent()) return;
    const body = new URLSearchParams({ event: JSON.stringify(payload) });
    try {
      if (typeof navigator.sendBeacon === 'function' && navigator.sendBeacon(endpoint.href, body)) {
        return;
      }
    } catch (_) {
      // Fall through to fetch when Beacon is unavailable or rejected.
    }
    void fetch(endpoint.href, {
      method: 'POST',
      body,
      credentials: 'omit',
      keepalive: true,
      mode: 'cors',
    }).catch(() => undefined);
  }

  let started = false;
  function start() {
    if (started || !analyticsAllowed()) return;
    started = true;

    const pagePath = (window.location.pathname || '/').slice(0, 512);
    if (PRIVATE_PATH_PATTERN.test(pagePath)) return;

    const visitorId = storedUuid(localStorage, VISITOR_KEY);
    const sessionId = storedUuid(sessionStorage, SESSION_KEY);
    const screenWidth = Math.max(0, Math.min(Number(window.screen?.width) || 0, 10000));
    const context = {
      visitor_id: visitorId,
      session_id: sessionId,
      page_path: pagePath,
      page_title: pagePath === '/search' ? null : cleanText(document.title, 255),
      referrer_host: referrerHost(),
      ...campaignValues(),
      device_type: deviceType(),
      locale: cleanText(navigator.language, 32),
      screen_width: screenWidth || null,
    };

    send({
      event_id: createUuid(),
      event_type: 'page_view',
      ...context,
      action: null,
      target: null,
      target_path: null,
    });

    document.addEventListener('click', (event) => {
      if (!(event.target instanceof Element)) return;
      const element = event.target.closest('a, button, [role="button"], input[type="submit"]');
      if (!element || element.closest('[data-analytics-ignore]')) return;
      const path = targetPath(element);
      const action = classifyClick(element, path);
      const target = clickLabel(element, action, path);
      if (
        action === 'product_click'
        && path
        && typeof window.Shopify?.analytics?.publish === 'function'
      ) {
        try {
          window.Shopify.analytics.publish('sunglens_product_clicked', {
            product_title: target,
            product_path: path,
          });
        } catch (_) {
          // The first-party click event below remains available as a fallback.
        }
      }
      send({
        event_id: createUuid(),
        event_type: 'click',
        ...context,
        action,
        target,
        target_path: path,
      });
    }, true);
  }

  document.addEventListener('visitorConsentCollected', start);
  if (typeof window.Shopify?.loadFeatures === 'function') {
    window.Shopify.loadFeatures(
      [{ name: 'consent-tracking-api', version: '0.1' }],
      (error) => {
        if (!error) start();
      },
    );
  } else {
    start();
  }
})();
